import { useEffect, useLayoutEffect, useRef, useState } from "react";
import fundo from "../assets/certificados/fundo-do-certificado.jpg";
import montserrat from "../assets/certificados/Montserrat-variavel.woff2";
import { dataDoDia } from "./FaculdadeUi";

// O certificado dos CURSOS GRATUITOS ao vivo (000380), o MESMO papel do certificados-cf: o fundo
// (`template-bg.jpg` de lá, 2668 × 1917) com o título, a mantenedora, a assinatura e os selos, e por
// cima o nome, a frase do curso, a data de conclusão e o QR da validação.
//
// Lá o PDF era desenhado no navegador (pdf-lib) em 640,32 × 460,08 pt, e o fundo trazia textos de
// exemplo no lugar da frase do curso e da data, que o PDF cobria com retângulos da cor do papel.
// Aqui é HTML sobre o mesmo fundo, com AS MESMAS medidas, tiradas do código de lá e convertidas em
// pixels do fundo (1 pt = 1917 / 460,08 px), e a impressão sai no mesmo tamanho de folha. As
// medidas estão em unidades do CONTÊINER (`cqw`), como no certificado do curso, para o texto
// acompanhar a largura na tela e na impressão.
//
// Cor FIXA de propósito: é papel, e papel não troca com o tema de quem o abre.

const LARGURA = 2668;
const PAPEL = "#ECE9E4";
const TINTA = "#000000";
/** px do fundo → cqw */
const c = (px: number) => `${((px * 100) / LARGURA).toFixed(4)}cqw`;
// Onde fica a linha de base de um texto de `line-height: 1` em Montserrat (ascendente 968 e
// descendente 251 em 1000): a 0,8585 do tamanho a partir do alto da caixa.
const BASE = 0.8585;

/** "completou com êxito o curso de" — sem repetir "curso" quando o nome já o tem, como lá. */
export function fraseDoCurso(nome: string): string {
  return /\bcursos?\b/i.test(nome) ? "completou com êxito o " : "completou com êxito o curso de ";
}

export default function CertificadoGratuito({
  nome,
  curso,
  horas,
  data,
  validacao,
}: {
  nome: string;
  curso: string;
  horas: number;
  /** a data de conclusão do CURSO, AAAA-MM-DD */
  data: string;
  /** o endereço que o QR leva */
  validacao: string;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [escala, setEscala] = useState(1);
  const nomeRef = useRef<HTMLParagraphElement>(null);

  // A fonte e a folha da impressão existem só enquanto a peça está na tela. É um efeito de LAYOUT,
  // e vem antes da medida do nome: num efeito comum a fonte só era declarada depois de medir, e a
  // medida via a fonte de reserva, mais estreita (o nome longo passava da linha).
  useLayoutEffect(() => {
    const estilo = document.createElement("style");
    estilo.setAttribute("data-certificado-gratuito-impressao", "");
    estilo.textContent = `@font-face {
  font-family: "Montserrat Certificado";
  src: url("${montserrat}") format("woff2");
  font-weight: 100 900;
  font-display: block;
}
@media print {
  @page { size: 640.32pt 460.08pt; margin: 0; }
  html, body { height: 460.08pt !important; overflow: hidden !important; }
  .certificado-gratuito {
    width: 640.32pt !important; height: 460.08pt !important; max-width: none !important;
    border-radius: 0 !important; box-shadow: none !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
}`;
    document.head.appendChild(estilo);
    return () => estilo.remove();
  }, []);

  // O QR da validação, com as cores de lá. A biblioteca só é baixada aqui.
  useEffect(() => {
    let vivo = true;
    void import("qrcode")
      .then((q) => q.toDataURL(validacao, { margin: 1, scale: 6, color: { dark: "#102850ff", light: "#ece9e4ff" } }))
      .then((url) => {
        if (vivo) setQr(url);
      })
      .catch(() => {
        if (vivo) setQr(null);
      });
    return () => {
      vivo = false;
    };
  }, [validacao]);

  // O nome numa linha só, como lá; o nome longo diminui até caber, em vez de entrar nos selos. A
  // medida não mexe no estilo do elemento (o React o reescreve só quando a escala muda, e um estilo
  // zerado aqui deixaria o nome no tamanho padrão da página): ela lê a largura do TEXTO no tamanho
  // de agora e calcula a do tamanho cheio.
  useLayoutEffect(() => {
    const el = nomeRef.current;
    if (!el) return;
    const medir = () => {
      const largura = el.clientWidth;
      const atual = parseFloat(getComputedStyle(el).fontSize);
      if (!largura || !atual) return;
      const faixa = document.createRange();
      faixa.selectNodeContents(el);
      const texto = faixa.getBoundingClientRect().width;
      // o tamanho cheio é 100 px do fundo; a caixa do nome tem 2030 px do fundo
      const cheio = (largura * 100) / 2030;
      const natural = (texto * cheio) / atual;
      setEscala(natural > largura ? Math.max(0.55, largura / natural) : 1);
    };
    medir();
    // `load` pede a fonte e espera por ELA; `fonts.ready` resolve na hora quando nada está pendente.
    void document.fonts?.load('700 100px "Montserrat Certificado"', nome).then(medir, medir);
  }, [nome]);

  return (
    <div
      data-certificado-gratuito
      className="certificado-gratuito area-de-impressao relative w-full max-w-[900px] overflow-hidden rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.35)]"
      style={{
        aspectRatio: "2668 / 1917",
        backgroundColor: PAPEL,
        containerType: "inline-size",
        fontFamily: '"Montserrat Certificado", Montserrat, sans-serif',
        color: TINTA,
      }}
    >
      <img src={fundo} alt="" className="pointer-events-none absolute inset-0 size-full select-none" />

      {/* Os retângulos da cor do papel cobrem os textos de exemplo do fundo (a frase e a data). */}
      <div className="absolute" style={{ left: c(125), top: c(920), width: c(2135), height: c(310), backgroundColor: PAPEL }} />
      <div className="absolute" style={{ left: c(125), top: c(1230), width: c(515), height: c(95), backgroundColor: PAPEL }} />

      <p
        ref={nomeRef}
        data-certificado-nome
        className="absolute m-0 whitespace-nowrap"
        style={{
          left: c(175),
          top: c(866.7 - BASE * 100 * escala),
          width: c(2030),
          fontSize: c(100 * escala),
          lineHeight: 1,
          fontWeight: 700,
        }}
      >
        {nome}
      </p>

      <p
        data-certificado-curso
        className="absolute m-0"
        style={{ left: c(170), top: c(944.6), width: c(2030), fontSize: c(70.83), lineHeight: c(80), fontWeight: 400 }}
      >
        {fraseDoCurso(curso)}
        <strong style={{ fontWeight: 700 }}>{curso},</strong> com {horas} horas de duração.
      </p>

      <p
        data-certificado-data
        className="absolute m-0"
        style={{ left: c(170), top: c(1320 - BASE * 75), fontSize: c(75), lineHeight: 1, fontWeight: 700 }}
      >
        {dataDoDia(data)}
      </p>

      {qr && (
        <img
          src={qr}
          alt="QR code da validação deste certificado"
          data-certificado-qr
          className="absolute"
          style={{ left: c(1395.8), top: c(1533.7), width: c(250), height: c(250) }}
        />
      )}
    </div>
  );
}
